import { Fragment, useEffect, useMemo, useState } from "react";
import { useStudents } from "@/hooks/useStudents";
import { ALL_BRANDS, Brand, Student } from "@/types";
import { doc, updateDoc, collection, getDocs, runTransaction, writeBatch, addDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { orgQuery, withOrg } from "@/lib/org";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { geocodeAddress } from "@/lib/vworld";
import { reviewAddressWithAi } from "@/lib/addressReview";
import { deriveBuildingName } from "@/lib/excelParse";
import { BRAND_COLORS } from "@/lib/brands";
import { fmtHo } from "@/lib/format";
import { Link } from "react-router-dom";
import StudentEditor from "@/components/StudentEditor";
import { downloadXlsx } from "@/lib/exportXlsx";

import { normalizeGu } from "@/lib/region";
import { plainError } from "@/lib/plainError";
type StatusKind = "ok" | "ai_fixed" | "needs_review" | "failed" | "none";

function statusOf(s: Student): StatusKind {
  if (s.geocodeStatus) return s.geocodeStatus as StatusKind;
  if (s.lat && s.lng) return "ok";
  return "none";
}

export default function StudentsPage() {
  const [editing, setEditing] = useState<Student | "new" | null>(null);
  const [editMsg, setEditMsg] = useState("");
  const [gu, setGu] = useState<string>("");
  // 다른 화면에서 ?q=이름 으로 들어오면 그 학생을 바로 찾아 둔다(알림 실패 카드의 「번호 고치기」)
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const { students, loading } = useStudents(gu ? { gu } : {});
  // 권역 시군구 옵션 — 무필터 로드 시점의 distinct gu를 보관(전국화, 하드코딩 대체)
  const [guOptions, setGuOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!gu && students.length) {
      setGuOptions(Array.from(new Set(students.map((s) => s.gu).filter(Boolean))).sort());
    }
  }, [students, gu]);
  const [geocoding, setGeocoding] = useState(false);
  const [geoLog, setGeoLog] = useState<string>("");
  const [rematchDong, setRematchDong] = useState(false);
  const [dongLog, setDongLog] = useState<string>("");
  const [showDelete, setShowDelete] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delLog, setDelLog] = useState<string>("");
  // 배송메모 편집 중인 학생 id와 입력값
  const [memoEditId, setMemoEditId] = useState<string>("");
  const [memoText, setMemoText] = useState<string>("");
  const [rowBusy, setRowBusy] = useState<string>(""); // 처리 중인 학생 id

  const filtered = useMemo(() => {
    const term = q.trim();
    if (!term) return students;
    return students.filter(
      (s) =>
        s.name.includes(term) ||
        s.school.includes(term) ||
        s.refinedAddress?.includes(term) ||
        s.phone?.includes(term),
    );
  }, [students, q]);

  const needsCheckCount = useMemo(
    () => students.filter((s) => s.geocodeNeedsCheck).length,
    [students],
  );
  const reviewCount = useMemo(
    () => students.filter((s) => s.geocodeStatus === "needs_review" || s.geocodeStatus === "failed").length,
    [students],
  );

  const runGeocode = async () => {
    setGeocoding(true);
    try {
      let ok = 0, aiFixed = 0, review = 0, fail = 0;
      const targets = filtered.filter(
        (s) => (!s.lat || !s.lng) && s.geocodeStatus !== "needs_review",
      );
      for (let i = 0; i < targets.length; i++) {
        const s = targets[i];
        setGeoLog(`진행 ${i + 1}/${targets.length} — ${s.school} ${s.name}`);
        try {
          const p = await geocodeAddress(s.refinedAddress || s.rawAddress);
          if (p && p.roadMismatch) {
            // 입력과 다른 번길로 스냅됨(함정 #8) — 지도 위치를 쓰지 않고 검토 대기로 넘긴다
            await updateDoc(doc(db, "students", s.id), {
              geocodeStatus: "needs_review",
              geocodeNeedsCheck: false,
              geocodeReasoning: `지도 검색이 입력 주소와 다른 길(${p.matched})로 매칭 — 주소 표기(번길 숫자·띄어쓰기) 확인 필요`,
              geocodeCandidates: [],
            });
            review++;
            continue;
          }
          if (p) {
            await updateDoc(doc(db, "students", s.id), {
              lat: p.y,
              lng: p.x,
              geocodedAt: new Date(),
              geocodeStatus: "ok",
              geocodeNeedsCheck: false,
            });
            ok++;
            continue;
          }
          // 자동 실패 → AI 검토
          setGeoLog(`AI 검토 ${i + 1}/${targets.length} — ${s.school} ${s.name}`);
          const result = await reviewAddressWithAi({
            rawAddress: s.rawAddress,
            refinedAddress: s.refinedAddress,
            school: s.school,
            gu: s.gu,
            dong: s.dong,
            buildingName: s.buildingName,
          });
          if (result.status === "ai_fixed" && result.fixed) {
            const bn = deriveBuildingName(result.fixed.address);
            await updateDoc(doc(db, "students", s.id), {
              refinedAddress: result.fixed.address,
              ...(bn ? { buildingName: bn } : {}),
              lat: result.fixed.lat,
              lng: result.fixed.lng,
              geocodedAt: new Date(),
              geocodeStatus: "ai_fixed",
              geocodeNeedsCheck: true,
              geocodeReasoning: result.fixed.reasoning,
              geocodeOriginalAddress: result.fixed.original,
              geocodeAssumptions: result.assumptions || [],
              geocodeCandidates: [],
            });
            aiFixed++;
          } else {
            await updateDoc(doc(db, "students", s.id), {
              geocodeStatus: result.status,
              geocodeNeedsCheck: false,
              geocodeCandidates: result.candidates || [],
              geocodeReasoning: result.aiReasoning || "",
              geocodeAssumptions: result.assumptions || [],
            });
            review++;
          }
        } catch (e: any) {
          console.warn("지오코딩 실패", s.id, e);
          fail++;
        }
      }
      setGeoLog(
        `완료 — 자동 ${ok}, AI수정 ${aiFixed}(체크대기), 검토필요 ${review}, 에러 ${fail}`,
      );
    } finally {
      setGeocoding(false);
    }
  };

  // 지도 위치는 있는데 동 이름(dong)이 빈 학생을 V-World 역지오코딩으로 채움.
  const missingDongCount = useMemo(
    () => students.filter((s) => s.lat && s.lng && !s.dong).length,
    [students],
  );
  const runDongRematch = async () => {
    setRematchDong(true);
    try {
      const targets = students.filter((s) => s.lat && s.lng && !s.dong);
      if (targets.length === 0) { setDongLog("동 이름 없는 학생이 없습니다."); return; }
      const fn = httpsCallable<{ lat: number; lng: number }, { gu: string; dong: string }>(functions, "reverseDong");
      let ok = 0, fail = 0;
      for (let i = 0; i < targets.length; i++) {
        const s = targets[i];
        setDongLog(`진행 ${i + 1}/${targets.length} — ${s.school} ${s.name}`);
        try {
          const r = await fn({ lat: s.lat!, lng: s.lng! });
          if (r.data.dong) {
            await updateDoc(doc(db, "students", s.id), { dong: r.data.dong, gu: normalizeGu(r.data.gu) || s.gu });
            ok++;
          } else fail++;
        } catch (e) { fail++; }
      }
      setDongLog(`완료 — 동 이름 채움 ${ok} / 실패 ${fail}`);
    } finally {
      setRematchDong(false);
    }
  };

  const confirmAiFix = async (s: Student) => {
    await updateDoc(doc(db, "students", s.id), {
      geocodeNeedsCheck: false,
      geocodeStatus: "ok",
    });
  };

  // students 컬렉션 전체 삭제 — 현재 구/검색 필터와 무관하게 모든 학생 제거
  const deleteAll = async () => {
    if (delConfirm !== "전체삭제") return;
    setDeleting(true);
    setDelLog("삭제 중…");
    try {
      const snap = await getDocs(orgQuery("students"));
      const ids = snap.docs.map((d) => d.id);
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach((id) => batch.delete(doc(db, "students", id)));
        await batch.commit();
        setDelLog(`삭제 중… ${Math.min(i + 450, ids.length)}/${ids.length}`);
      }
      setDelLog(`✅ ${ids.length}명 전체 삭제 완료`);
      setShowDelete(false);
      setDelConfirm("");
    } catch (e: any) {
      setDelLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setDeleting(false);
    }
  };

  const toggleCourier = async (s: Student) => {
    await updateDoc(doc(db, "students", s.id), { courierFlag: !s.courierFlag });
  };

  // 배송메모 저장 — 기사가 배송 시 반드시 확인하는 메모
  const saveMemo = async (s: Student) => {
    setRowBusy(s.id);
    try {
      await updateDoc(doc(db, "students", s.id), { deliveryMemo: memoText.trim(), updatedAt: serverTimestamp() });
      setMemoEditId("");
      setMemoText("");
    } finally {
      setRowBusy("");
    }
  };

  // 전출 처리 — 학생 1명을 명단에서 빼고 studentDeletions에 문서를 통째로 보관한다(되살리기용, 예행연습 02-16).
  const transferOut = async (s: Student) => {
    if (!window.confirm(`${s.school} ${s.name} 학생을 전출 처리합니다.\n명단에서 빠지고, 「전출 학생 되살리기」에서 되돌릴 수 있습니다. 계속할까요?`)) return;
    setRowBusy(s.id);
    try {
      const { id: _omit, ...before } = s;
      await addDoc(collection(db, "studentDeletions"), withOrg({
        studentId: s.id,
        name: s.name,
        school: s.school,
        refinedAddress: s.refinedAddress || "",
        reason: "학생관리 전출 처리",
        deletedAt: serverTimestamp(),
        before,
      }));
      await deleteDoc(doc(db, "students", s.id));
      setEditMsg(`${s.school} ${s.name} 전출 처리했습니다. 잘못 눌렀으면 「전출 학생 되살리기」에서 되돌리세요.`);
    } catch (e: any) {
      alert(`전출 처리 실패: ${plainError(e)}`);
    } finally {
      setRowBusy("");
    }
  };

  // 명단 내려받기 — 지금 필터에 보이는 학생을 엑셀로(예행연습 03-12). 정리된 주소·동호·상태까지 담는다
  const exportStudents = () => {
    const rows = filtered.map((s) => ({
      학교: s.school || "", 이름: s.name || "", 보호자: s.guardian || "", 전화: s.phone || "",
      원본주소: s.rawAddress || "", 정리주소: s.refinedAddress || "", 우편번호: s.postalCode || "",
      구: s.gu || "", 동: s.dong || "", 건물: s.buildingName || "", 건물동: s.buildingDong || "", 호: s.buildingHo || "",
      브랜드: s.brand || "", 수량: s.boxesPerMonth ?? 1,
      // 「쉼」 버튼은 없앴다(대표 결정 Q25, 안 받는 학생은 전출). 옛 쉼 학생은 scripts/pausedStudents.cjs로 옮기기 전까지 배송이 막혀 있어 사실대로 적는다
      상태: s.status === "paused" ? "배송 멈춤(옛 쉼)" : s.courierFlag ? (s.courierReason === "always" ? "택배(항상)" : "택배(이번 달)") : "직배송",
      배송메모: s.deliveryMemo || "", 메모: s.note || "", 배송한달: (s.deliveredMonths || []).join(" "),
    }));
    downloadXlsx(`학생명단_${new Date().toISOString().slice(0, 10)}_${rows.length}명.xlsx`, "학생 명단", rows);
  };

  // 전출 학생 되살리기 — 보관해 둔 문서를 같은 ID로 되돌린다. 같은 ID가 이미 있으면(다시 올린 명단) 덮지 않는다.
  type Deletion = { id: string; studentId: string; name: string; school: string; refinedAddress: string; reason: string; deletedAt?: { toMillis?: () => number }; restoredAt?: unknown; before?: Record<string, unknown> };
  const [deleted, setDeleted] = useState<Deletion[] | null>(null);
  const loadDeleted = async () => {
    const snap = await getDocs(orgQuery("studentDeletions"));
    const list = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<Deletion, "id">) }))
      .filter((d) => !d.restoredAt)
      .sort((a, b) => (b.deletedAt?.toMillis?.() ?? 0) - (a.deletedAt?.toMillis?.() ?? 0))
      .slice(0, 100);
    setDeleted(list);
  };
  const restore = async (d: Deletion) => {
    if (!d.before) { alert("옛 방식으로 전출 처리된 학생이라 되살릴 내용이 없습니다. 「학생 한 명 추가」로 다시 넣어 주세요."); return; }
    setRowBusy(d.id);
    try {
      const ref = doc(db, "students", d.studentId);
      // 존재 확인과 복원을 한 트랜잭션에서 — 그 사이 다른 관리자가 같은 ID를 만들면 덮어쓰지 않는다(코덱스 검토)
      const restored = await runTransaction(db, async (tx) => {
        if ((await tx.get(ref)).exists()) return false;
        tx.set(ref, withOrg({ ...d.before, updatedAt: serverTimestamp() }));
        tx.update(doc(db, "studentDeletions", d.id), withOrg({ restoredAt: serverTimestamp() }));
        return true;
      });
      if (!restored) { alert(`${d.school} ${d.name} 학생이 이미 명단에 있습니다(다시 올린 명단). 되살리지 않았습니다.`); return; }
      setDeleted((cur) => (cur || []).filter((x) => x.id !== d.id));
      setEditMsg(`${d.school} ${d.name} 학생을 되살렸습니다.`);
    } catch (e: any) {
      alert(`되살리기 실패: ${plainError(e)}`);
    } finally {
      setRowBusy("");
    }
  };
  const updateBrand = async (s: Student, brand: Brand) => {
    await updateDoc(doc(db, "students", s.id), { brand });
  };
  const updateCompany = async (s: Student, company: string) => {
    await updateDoc(doc(db, "students", s.id), { company });
  };

  return (
    <div className="space-y-4">
      {editing && (
        <StudentEditor
          student={editing === "new" ? undefined : editing}
          onClose={(msg) => { setEditing(null); if (msg) setEditMsg(msg); }}
        />
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold">👨‍🎓 학생 관리</h1>
        <button className="btn-primary" onClick={() => { setEditMsg(""); setEditing("new"); }}>+ 학생 한 명 추가</button>
        <button className="btn-secondary" onClick={() => (deleted ? setDeleted(null) : loadDeleted())}>↩️ 전출 학생 되살리기</button>
        <button className="btn-secondary" onClick={exportStudents} disabled={!filtered.length} title="지금 보이는 학생을 엑셀 파일로 받습니다(교육청 제출·해지 전 보관용)">📄 명단 내려받기 ({filtered.length}명)</button>
        <div className="flex gap-2 text-xs">
          {needsCheckCount > 0 && (
            <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">
              🤖 AI 수정 체크대기 {needsCheckCount}명
            </span>
          )}
          {reviewCount > 0 && (
            <Link
              to="/admin/address-review"
              className="px-2 py-1 rounded bg-rose-100 text-rose-800 hover:bg-rose-200"
            >
              ⚠️ 주소 검토 필요 {reviewCount}명 →
            </Link>
          )}
        </div>
      </div>

      {editMsg && <div className="card p-3 text-sm text-emerald-700" role="status">{editMsg}</div>}

      {deleted && (
        <div className="card p-4 text-sm space-y-2">
          <div className="font-semibold">전출 처리한 학생 {deleted.length}명 — 잘못 눌렀으면 되살리세요</div>
          {deleted.length === 0 && <p className="text-slate-500">되살릴 학생이 없습니다.</p>}
          <ul className="divide-y max-h-64 overflow-auto">
            {deleted.map((d) => (
              <li key={d.id} className="py-2 flex items-center gap-2 flex-wrap">
                <span className="text-slate-500 text-xs">{d.deletedAt?.toMillis ? new Date(d.deletedAt.toMillis()).toLocaleDateString("ko-KR") : ""}</span>
                <span><b>{d.school} {d.name}</b> <span className="text-slate-500">{d.refinedAddress}</span></span>
                <button className="btn-secondary text-xs ml-auto min-h-[36px]" onClick={() => restore(d)} disabled={rowBusy === d.id || !d.before}>
                  {d.before ? "되살리기" : "되살릴 내용 없음"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card p-4 flex flex-wrap items-center gap-3 text-sm">
        <select className="input max-w-[160px]" value={gu} onChange={(e) => setGu(e.target.value)}>
          <option value="">전체 구</option>
          {guOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <input
          className="input max-w-sm"
          placeholder="이름 / 학교 / 주소 / 연락처 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="text-slate-500 ml-auto">총 {filtered.length}명</div>
        <button className="btn-secondary" onClick={runGeocode} disabled={geocoding}>
          {geocoding ? "위치 찾는 중…" : "지도 위치 찾기"}
        </button>
        <button className="btn-secondary" onClick={runDongRematch} disabled={rematchDong || missingDongCount === 0}>
          {rematchDong ? "찾는 중…" : `동 이름 다시 찾기${missingDongCount ? ` (${missingDongCount})` : ""}`}
        </button>
        <button
          className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-rose-100 text-rose-700 hover:bg-rose-200"
          onClick={() => setShowDelete((v) => !v)}
        >
          전체 삭제
        </button>
      </div>

      {geoLog && <div className="text-xs text-slate-500">{geoLog}</div>}
      {dongLog && <div className="text-xs text-slate-500">{dongLog}</div>}

      {showDelete && (
        <div className="card p-4 border border-rose-300 bg-rose-50 space-y-3">
          <div className="text-sm font-semibold text-rose-800">⚠️ 학생 데이터 전체 삭제 (되돌릴 수 없음)</div>
          <p className="text-xs text-rose-700 leading-relaxed">
            현재 구/검색 필터와 무관하게 <strong>이 업체의 모든 학생</strong>이 삭제됩니다.
            복구할 수 없으니, 계속하려면 아래에 <strong>전체삭제</strong> 라고 입력하세요.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              className="input max-w-[160px]"
              placeholder="전체삭제"
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
            />
            <button
              className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-rose-600 text-white disabled:opacity-40"
              disabled={delConfirm !== "전체삭제" || deleting}
              onClick={deleteAll}
            >
              {deleting ? "삭제 중…" : "영구 삭제"}
            </button>
            <button
              className="btn-ghost text-sm"
              onClick={() => { setShowDelete(false); setDelConfirm(""); }}
            >
              취소
            </button>
          </div>
          {delLog && <div className="text-xs text-rose-700">{delLog}</div>}
        </div>
      )}

      <div className="card overflow-auto max-h-[65vh]">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-2">상태</th>
              <th className="p-2">학교</th>
              <th className="p-2">학생</th>
              <th className="p-2">보호자</th>
              <th className="p-2">연락처</th>
              <th className="p-2">동</th>
              <th className="p-2">브랜드</th>
              <th className="p-2">회사</th>
              <th className="p-2">주소</th>
              <th className="p-2">지도 위치</th>
              <th className="p-2 text-center">월말택배</th>
              <th className="p-2 text-center sticky right-0 bg-slate-50">작업</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="p-4 text-center text-slate-400">로딩…</td></tr>}
            {filtered.slice(0, 500).map((s) => {
              const st = statusOf(s);
              return (
                <Fragment key={s.id}>
                <tr className="border-t">
                  <td className="p-2 whitespace-nowrap">
                    <StatusBadge status={st} needsCheck={!!s.geocodeNeedsCheck} />
                    {s.geocodeNeedsCheck && (
                      <button
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        title={`AI 추론: ${s.geocodeReasoning || "(이유 없음)"}`}
                        onClick={() => confirmAiFix(s)}
                      >
                        확인
                      </button>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap">{s.school}</td>
                  <td className="p-2 font-medium whitespace-nowrap">{s.name}</td>
                  <td className="p-2 whitespace-nowrap">{s.guardian}</td>
                  <td className="p-2 whitespace-nowrap">
                    {s.phone}
                    {s.notifyOptOut && <span className="ml-1 chip bg-slate-200 text-slate-600 text-[10px]" title="알림 받지 않음 — 「고치기」에서 다시 켤 수 있습니다">알림 끔</span>}
                  </td>
                  <td className="p-2 whitespace-nowrap">{s.dong}</td>
                  <td className="p-2">
                    <select
                      value={s.brand}
                      onChange={(e) => updateBrand(s, e.target.value as Brand)}
                      className={`text-xs rounded px-1 py-0.5 ${BRAND_COLORS[s.brand] ?? "bg-slate-100"}`}
                    >
                      {ALL_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className="p-2">
                    <input
                      className="input text-xs w-20 py-0.5"
                      value={s.company}
                      onChange={(e) => updateCompany(s, e.target.value)}
                    />
                  </td>
                  <td className="p-2 text-slate-500 min-w-[250px]">
                    {s.refinedAddress}
                    {(s.buildingName || s.buildingDong || s.buildingHo) && (
                      <div className="text-[10px] text-brand-600">
                        🏢 {s.buildingName}
                        {s.buildingDong && ` ${s.buildingDong}동`}
                        {s.buildingHo && ` ${fmtHo(s.buildingHo)}`}
                      </div>
                    )}
                    {s.geocodeOriginalAddress && s.geocodeOriginalAddress !== s.refinedAddress && (
                      <div className="text-[10px] text-slate-400 italic">
                        원본: {s.geocodeOriginalAddress}
                      </div>
                    )}
                    {/* 메모 내용이 버튼 title(마우스 툴팁)에만 있어 휴대폰에선 볼 수 없었다 → 목록에 직접 노출 */}
                    {s.deliveryMemo && (
                      <div className="text-[10px] font-semibold text-amber-700 break-keep">📌 {s.deliveryMemo}</div>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap text-slate-400">
                    {s.lat ? `${s.lat.toFixed(4)},${s.lng?.toFixed(4)}` : "—"}
                  </td>
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={!!s.courierFlag} onChange={() => toggleCourier(s)} />
                  </td>
                  <td className="p-2 whitespace-nowrap sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                    <div className="flex items-center gap-1">
                      <button
                        className={`text-[10px] px-1.5 py-0.5 rounded ${s.deliveryMemo ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        title={s.deliveryMemo ? `배송메모: ${s.deliveryMemo}` : "배송메모 추가 — 기사가 배송 시 확인"}
                        onClick={() => { setMemoEditId(memoEditId === s.id ? "" : s.id); setMemoText(s.deliveryMemo || ""); }}
                      >
                        {s.deliveryMemo ? "📝메모" : "메모"}
                      </button>
                      <Link
                        to="/admin/address-updates"
                        className="text-[10px] px-1.5 py-0.5 rounded bg-milk-100 text-milk-700 hover:bg-milk-200"
                        title="주소변경 페이지로 이동"
                      >
                        주소변경
                      </Link>
                      <button
                        className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                        onClick={() => { setEditMsg(""); setEditing(s); }}
                      >
                        고치기
                      </button>
                      <button
                        className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-40"
                        title="전출 — 명단에서 빠집니다. 「전출 학생 되살리기」로 되돌릴 수 있습니다"
                        onClick={() => transferOut(s)}
                        disabled={rowBusy === s.id}
                      >
                        전출
                      </button>
                    </div>
                  </td>
                </tr>
                {memoEditId === s.id && (
                  <tr className="bg-amber-50">
                    <td colSpan={12} className="p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-amber-800 whitespace-nowrap">📝 배송메모 (기사 필독)</span>
                        <input
                          className="input text-xs flex-1 py-1"
                          placeholder="예: 부재 시 경비실 맡겨주세요 / 반려견 주의 / 초인종 대신 노크"
                          value={memoText}
                          onChange={(e) => setMemoText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveMemo(s); }}
                          autoFocus
                        />
                        <button
                          className="btn-primary btn-sm text-xs px-2 py-1 disabled:opacity-40"
                          onClick={() => saveMemo(s)}
                          disabled={rowBusy === s.id}
                        >
                          저장
                        </button>
                        <button className="btn-ghost text-xs px-2 py-1" onClick={() => { setMemoEditId(""); setMemoText(""); }}>취소</button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
              })}
            {filtered.length > 500 && (
              <tr><td colSpan={12} className="p-2 text-center text-slate-400">… 나머지 {filtered.length - 500}명은 필터로 좁혀 주세요</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status, needsCheck }: { status: StatusKind; needsCheck: boolean }) {
  if (status === "ok") return <span title="지도 위치 찾음" className="text-emerald-600">✅</span>;
  if (status === "ai_fixed") {
    return (
      <span title={needsCheck ? "AI 수정 — 체크 대기" : "AI 수정 확인됨"} className={needsCheck ? "text-amber-600" : "text-emerald-600"}>
        {needsCheck ? "🤖" : "✅"}
      </span>
    );
  }
  if (status === "needs_review") return <span title="AI 도 확신 못 함 — 수동 검토 필요" className="text-rose-600">⚠️</span>;
  if (status === "failed") return <span title="AI 호출 실패" className="text-rose-600">❌</span>;
  return <span title="아직 위치 못 찾음" className="text-slate-300">—</span>;
}
